import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import LearnerPortalShell from "@/components/learner/LearnerPortalShell";
import {
  getLesson,
  markLessonComplete,
  CATEGORY_META,
  type LessonDetail,
  type LessonBlock,
  type CountryDiff,
} from "@/lib/training-api";
import { getProfile } from "@/lib/profile-api";

type LoadState =
  | { kind: "loading" }
  | { kind: "ready"; lesson: LessonDetail; homeCountry: string }
  | { kind: "error" };

const SEVERITY_STYLES: Record<string, string> = {
  major: "border-red-300 bg-red-50 text-red-900",
  moderate: "border-amber-300 bg-amber-50 text-amber-900",
  minor: "border-blue-200 bg-blue-50 text-blue-900",
};
const SEVERITY_BADGE: Record<string, string> = {
  major: "bg-red-100 text-red-700",
  moderate: "bg-amber-100 text-amber-700",
  minor: "bg-blue-100 text-blue-700",
};

export default function LessonPage() {
  const { t } = useTranslation();
  const { lessonId } = useParams<{ lessonId: string }>();
  const [loadState, setLoadState] = useState<LoadState>({ kind: "loading" });
  const [marking, setMarking] = useState(false);

  useEffect(() => {
    if (!lessonId) return;
    let cancelled = false;
    (async () => {
      try {
        const [lesson, profile] = await Promise.all([
          getLesson(lessonId),
          getProfile(),
        ]);
        if (cancelled) return;
        setLoadState({ kind: "ready", lesson, homeCountry: profile?.home_country ?? "" });
      } catch {
        if (!cancelled) setLoadState({ kind: "error" });
      }
    })();
    return () => { cancelled = true; };
  }, [lessonId]);

  const handleToggleComplete = async () => {
    if (loadState.kind !== "ready") return;
    setMarking(true);
    const next = !loadState.lesson.completed;
    try {
      await markLessonComplete(loadState.lesson.id, next);
      setLoadState({ ...loadState, lesson: { ...loadState.lesson, completed: next } });
    } finally {
      setMarking(false);
    }
  };

  if (loadState.kind === "loading") {
    return (
      <LearnerPortalShell pageTitle={t("nav.training")}>
        <div className="mx-auto max-w-5xl px-4 py-16 sm:px-8">
          <div className="space-y-4 animate-pulse">
            <div className="h-6 w-48 rounded bg-slate-200" />
            <div className="h-48 rounded-2xl bg-slate-200" />
            <div className="h-4 w-3/4 rounded bg-slate-200" />
            <div className="h-4 w-1/2 rounded bg-slate-200" />
          </div>
        </div>
      </LearnerPortalShell>
    );
  }

  if (loadState.kind === "error") {
    return (
      <LearnerPortalShell pageTitle={t("nav.training")}>
        <div className="mx-auto max-w-3xl px-4 py-16 text-center sm:px-8">
          <p className="text-lg font-semibold text-slate-700">Lesson not found</p>
          <Link to="/training" className="mt-4 inline-block text-brand-primary underline">
            Back to Training
          </Link>
        </div>
      </LearnerPortalShell>
    );
  }

  const { lesson, homeCountry } = loadState;
  const meta = CATEGORY_META[lesson.category];

  const relevantDiff = homeCountry
    ? lesson.country_diffs.find((d) => d.country_code === homeCountry) ??
      lesson.country_diffs[0]
    : lesson.country_diffs[0];

  const relatedLessons = lesson.related_lesson_ids;

  return (
    <LearnerPortalShell pageTitle={lesson.title}>
      <div className="bg-slate-50">
      {/* ── Breadcrumb ─────────────────────────────────────────────── */}
      <div className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-5xl items-center gap-2 px-4 py-3 text-sm sm:px-8">
          <Link to="/training" className="text-brand-primary hover:underline no-underline">
            Training
          </Link>
          <span className="text-slate-400">/</span>
          <span className="text-slate-500">{meta.emoji} {meta.label}</span>
          <span className="text-slate-400">/</span>
          <span className="font-medium text-slate-700 truncate">{lesson.title}</span>
        </div>
      </div>

      <div className="mx-auto max-w-5xl px-4 py-8 sm:px-8">
        <div className="flex flex-col gap-8 lg:flex-row lg:items-start">

          {/* ── Main Content ───────────────────────────────────────── */}
          <div className="flex-1 min-w-0">
            {/* Hero */}
            <div
              className={`relative flex h-48 items-center justify-center rounded-2xl bg-gradient-to-br ${lesson.colorFrom} ${lesson.colorTo} mb-6 overflow-hidden`}
            >
              <span className="text-8xl">{lesson.emoji}</span>
              <div className="absolute inset-0 bg-black/10" />
              <div className="absolute bottom-4 left-5 right-5 flex items-end justify-between">
                <span className="rounded-full bg-white/20 px-3 py-1 text-xs font-semibold text-white backdrop-blur">
                  {meta.emoji} {meta.label}
                </span>
                <span className="text-xs text-white/80">{lesson.duration_min} min read</span>
              </div>
            </div>

            {/* Title + complete button */}
            <div className="flex flex-wrap items-start gap-4 mb-2">
              <h1 className="flex-1 font-display text-2xl font-bold text-slate-900 sm:text-3xl leading-tight">
                {lesson.title}
              </h1>
              <button
                onClick={handleToggleComplete}
                disabled={marking}
                className={`shrink-0 flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-all ${
                  lesson.completed
                    ? "bg-green-100 text-green-700 hover:bg-green-200"
                    : "bg-brand-primary text-white hover:bg-brand-primary/90"
                }`}
              >
                {lesson.completed ? (
                  <>
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                    Completed
                  </>
                ) : (
                  marking ? "Saving…" : "Mark as complete"
                )}
              </button>
            </div>

            <p className="text-base text-slate-600 mb-8">{lesson.summary}</p>

            {/* Content blocks */}
            <div className="space-y-6">
              {lesson.content.map((block, i) => (
                <ContentBlock key={i} block={block} />
              ))}
            </div>

            {/* Navigation footer */}
            <div className="mt-12 flex items-center gap-4 pt-6 border-t border-slate-200">
              <Link
                to="/training"
                className="text-sm font-medium text-brand-primary hover:underline no-underline"
              >
                ← Back to all lessons
              </Link>
              <button
                onClick={handleToggleComplete}
                disabled={marking || lesson.completed}
                className={`ml-auto rounded-full px-5 py-2.5 text-sm font-semibold transition-all ${
                  lesson.completed
                    ? "bg-green-50 text-green-700 cursor-default"
                    : "bg-brand-primary text-white hover:bg-brand-primary/90"
                }`}
              >
                {lesson.completed ? "✓ Lesson complete" : "Mark as complete →"}
              </button>
            </div>
          </div>

          {/* ── Sidebar ─────────────────────────────────────────────── */}
          <aside className="w-full lg:w-80 shrink-0 space-y-5">
            {/* Country difference panel */}
            {lesson.country_diffs.length > 0 && (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xl">🌍</span>
                  <h2 className="font-semibold text-amber-900 text-sm">
                    How this differs from your home country
                  </h2>
                </div>
                {relevantDiff ? (
                  <CountryDiffCard diff={relevantDiff} expanded />
                ) : null}
                {lesson.country_diffs.length > 1 && (
                  <details className="mt-3">
                    <summary className="text-xs font-medium text-amber-700 cursor-pointer hover:text-amber-900">
                      View other countries ({lesson.country_diffs.length - 1} more)
                    </summary>
                    <div className="mt-3 space-y-3">
                      {lesson.country_diffs
                        .filter((d) => d !== relevantDiff)
                        .map((d) => (
                          <CountryDiffCard key={d.country_code} diff={d} />
                        ))}
                    </div>
                  </details>
                )}
              </div>
            )}

            {/* Tags */}
            {lesson.tags.length > 0 && (
              <div className="rounded-2xl border border-slate-200 bg-white p-5">
                <h2 className="text-sm font-semibold text-slate-700 mb-3">Topics covered</h2>
                <div className="flex flex-wrap gap-2">
                  {lesson.tags.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Progress in category */}
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <h2 className="text-sm font-semibold text-slate-700 mb-3">
                {meta.emoji} {meta.label}
              </h2>
              <p className="text-sm text-slate-500">
                This lesson is part of the <strong>{meta.label}</strong> topic.
              </p>
              <Link
                to={`/training?category=${lesson.category}`}
                className="mt-3 block text-sm font-medium text-brand-primary hover:underline no-underline"
              >
                See all {meta.label} lessons →
              </Link>
            </div>

            {/* Related lessons */}
            {relatedLessons.length > 0 && (
              <div className="rounded-2xl border border-slate-200 bg-white p-5">
                <h2 className="text-sm font-semibold text-slate-700 mb-3">Up next</h2>
                <div className="space-y-2">
                  {relatedLessons.map((id) => (
                    <Link
                      key={id}
                      to={`/training/${id}`}
                      className="flex items-center gap-2 rounded-lg p-2 text-sm font-medium text-slate-700 hover:bg-slate-50 no-underline transition-colors"
                    >
                      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-slate-100 text-xs">→</span>
                      {id.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </aside>
        </div>
      </div>
      </div>
    </LearnerPortalShell>
  );
}

function ContentBlock({ block }: { block: LessonBlock }) {
  switch (block.type) {
    case "heading":
      return (
        <h2 className="font-display text-xl font-bold text-slate-900 mt-2">{block.text}</h2>
      );
    case "text":
      return (
        <p className="text-base leading-relaxed text-slate-700">{block.body}</p>
      );
    case "tip":
      return (
        <div className="rounded-xl border border-teal-200 bg-teal-50 p-4">
          <p className="text-xs font-bold uppercase tracking-wide text-teal-700 mb-1">
            {block.title ?? "💡 Tip"}
          </p>
          <p className="text-sm text-teal-900 leading-relaxed">{block.body}</p>
        </div>
      );
    case "warning":
      return (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4">
          <p className="text-xs font-bold uppercase tracking-wide text-red-700 mb-1">
            {block.title ?? "⚠️ Important"}
          </p>
          <p className="text-sm text-red-900 leading-relaxed">{block.body}</p>
        </div>
      );
    case "steps":
      return (
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          {block.title && (
            <p className="text-sm font-semibold text-slate-700 mb-3">{block.title}</p>
          )}
          <ol className="space-y-3">
            {block.items.map((item, i) => (
              <li key={i} className="flex gap-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-primary text-xs font-bold text-white">
                  {i + 1}
                </span>
                <span className="text-sm text-slate-700 pt-0.5 leading-relaxed">{item}</span>
              </li>
            ))}
          </ol>
        </div>
      );
    case "comparison":
      return (
        <div className="rounded-xl overflow-hidden border border-slate-200">
          <div className="grid grid-cols-2 divide-x divide-slate-200">
            <div className="bg-red-50 p-4">
              <p className="text-xs font-bold uppercase tracking-wide text-red-600 mb-2">
                {block.left_label}
              </p>
              <p className="text-sm text-slate-800 leading-relaxed">{block.left}</p>
            </div>
            <div className="bg-green-50 p-4">
              <p className="text-xs font-bold uppercase tracking-wide text-green-700 mb-2">
                {block.right_label}
              </p>
              <p className="text-sm text-slate-800 leading-relaxed">{block.right}</p>
            </div>
          </div>
        </div>
      );
    case "callout":
      return (
        <div className="rounded-xl border border-brand-primary/20 bg-brand-primary/5 p-4 flex gap-3">
          <span className="text-2xl shrink-0">{block.emoji}</span>
          <div>
            <p className="font-semibold text-brand-primary text-sm mb-1">{block.title}</p>
            <p className="text-sm text-slate-700 leading-relaxed">{block.body}</p>
          </div>
        </div>
      );
    case "image":
      return (
        <figure className="overflow-hidden rounded-xl border border-slate-200">
          <img
            src={block.src}
            alt={block.alt}
            className="w-full object-cover"
            loading="lazy"
          />
          {block.caption && (
            <figcaption className="bg-slate-50 px-4 py-2 text-xs text-slate-500 text-center">
              {block.caption}
            </figcaption>
          )}
        </figure>
      );
    default:
      return null;
  }
}

function CountryDiffCard({ diff, expanded = false }: { diff: CountryDiff; expanded?: boolean }) {
  return (
    <div className={`rounded-xl border p-3 ${expanded ? SEVERITY_STYLES[diff.severity] ?? SEVERITY_STYLES["minor"] : "border-amber-200 bg-amber-50/50"}`}>
      <div className="flex items-center gap-2 mb-1.5">
        <span className="text-lg">{diff.flag}</span>
        <span className="font-medium text-sm text-slate-800">{diff.country_name}</span>
        <span className={`ml-auto rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
          SEVERITY_BADGE[diff.severity] ?? SEVERITY_BADGE["minor"]
        }`}>
          {diff.severity}
        </span>
      </div>
      <p className="text-xs text-slate-700 leading-relaxed">{diff.summary}</p>
      {expanded && diff.details && (
        <p className="mt-2 text-xs text-slate-500 leading-relaxed border-t border-amber-200 pt-2">
          {diff.details}
        </p>
      )}
    </div>
  );
}
